
import { useState } from 'react';
import { BlogPost } from '@/types/blog';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';

export const useBlogManagement = (initialPosts: BlogPost[]) => {
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>(initialPosts);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredPosts, setFilteredPosts] = useState<BlogPost[]>(blogPosts);
  const [isGenerating, setIsGenerating] = useState(false);
  const { toast } = useToast();

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    
    if (term.trim() === '') {
      setFilteredPosts(blogPosts);
    } else {
      const filtered = blogPosts.filter(
        post => 
          post.title.toLowerCase().includes(term.toLowerCase()) || 
          post.category.toLowerCase().includes(term.toLowerCase()) ||
          post.author.toLowerCase().includes(term.toLowerCase())
      );
      setFilteredPosts(filtered);
    }
  };

  const filterByStatus = (status: string) => {
    if (status === 'all') {
      setFilteredPosts(blogPosts);
    } else {
      const filtered = blogPosts.filter(post => post.status === status);
      setFilteredPosts(filtered);
    }
  };

  const handleDelete = (id: string) => {
    const updatedPosts = blogPosts.filter(post => post.id !== id);
    setBlogPosts(updatedPosts);
    setFilteredPosts(updatedPosts);
    
    toast({
      title: "Post deleted",
      description: "The blog post has been removed successfully.",
    });
  };

  const handleToggleAutoSchedule = (id: string) => {
    const updatedPosts = blogPosts.map(post => {
      if (post.id === id) {
        return {
          ...post,
          autoScheduled: !post.autoScheduled
        };
      }
      return post;
    });
    
    setBlogPosts(updatedPosts);
    setFilteredPosts(applyCurrentFilters(updatedPosts));
    
    const targetPost = updatedPosts.find(post => post.id === id);
    
    toast({
      title: targetPost?.autoScheduled ? "Auto-schedule enabled" : "Auto-schedule disabled",
      description: targetPost?.autoScheduled 
        ? "This post will be automatically updated by AI." 
        : "This post will no longer be automatically updated.",
    });
  };

  const handleGenerateBlog = (prompt: string) => {
    if (!prompt.trim()) {
      toast({
        title: "Error",
        description: "Please provide a topic or prompt for the AI.",
        variant: "destructive"
      });
      return;
    }
    
    setIsGenerating(true);
    
    // Simulating AI generation - in a real app, this would call an API
    setTimeout(() => {
      const newId = (blogPosts.length + 1).toString();
      const newPost: BlogPost = {
        id: newId,
        title: `AI Generated: ${prompt.substring(0, 40)}...`,
        content: `This is an AI generated content about "${prompt}". The full content would be generated by an actual AI model in a production environment.`,
        status: 'draft',
        category: 'ai-generated',
        date: format(new Date(), 'yyyy-MM-dd'),
        author: 'AI Assistant',
        image: '/lovable-uploads/e2e58de0-5228-4c21-8754-a52246e544e0.png',
        readTime: '5 min',
        socialShare: true,
        autoScheduled: true
      };
      
      const updatedPosts = [...blogPosts, newPost];
      setBlogPosts(updatedPosts);
      setFilteredPosts(updatedPosts);
      
      setIsGenerating(false);
      
      toast({
        title: "Blog post created",
        description: "AI has generated a draft blog post. You can now edit it.",
      });

      return newPost;
    }, 2000);
  };

  const handleSavePost = (post: BlogPost) => {
    let updatedPosts: BlogPost[];
    
    if (!post.id || !blogPosts.find(p => p.id === post.id)) {
      // Add new post
      updatedPosts = [...blogPosts, post];
      toast({
        title: "Post created",
        description: "The new blog post has been created successfully."
      });
    } else {
      // Update existing post
      updatedPosts = blogPosts.map(p => 
        p.id === post.id ? post : p
      );
      toast({
        title: "Post updated",
        description: "The blog post has been updated successfully."
      });
    }
    
    setBlogPosts(updatedPosts);
    setFilteredPosts(applyCurrentFilters(updatedPosts));
  };

  const applyCurrentFilters = (posts: BlogPost[]) => {
    if (searchTerm.trim() === '') return posts;
    
    return posts.filter(post => 
      post.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
      post.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.author.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  return {
    blogPosts,
    filteredPosts,
    searchTerm,
    isGenerating,
    handleSearch,
    filterByStatus,
    handleDelete,
    handleToggleAutoSchedule,
    handleGenerateBlog,
    handleSavePost,
  };
};
